/*
 * Canevas d'entretien du candidat livreur — version 1 (3 octobre 2026).
 *
 * Source : SwiftEats/docs/onboarding/canevas-entretien-livreur.md, qui porte
 * aussi la version papier commentée. Corriger d'abord le document, puis ce
 * fichier, en incrémentant `version` : chaque entretien enregistre la version
 * du canevas qui l'a guidé.
 *
 * Ce fichier ne contient QUE des données. Il n'est importé qu'à un seul
 * endroit : `canevas.ts`, qui le valide (voir les règles dans
 * `canevas-types.ts`). Une question éliminatoire se déclenche sur « non »
 * (oui/non) ou sur une note de 1 : elles sont donc formulées pour que ce soit
 * la mauvaise réponse.
 */
import type { Canevas } from './canevas-types'

export const CANEVAS_ENTRETIEN: Canevas = {
  version: 1,
  sections: [
    {
      id: 'accueil',
      titre: 'Accueil (2 min)',
      consigne:
        "Présentez-vous, puis SwiftEats en une phrase : « Nous livrons des repas et des courses à Bamako, payés par mobile money. Les livreurs sont des partenaires indépendants, libres de leurs horaires. » Annoncez une demi-heure d'entretien, puis une session d'information et un petit questionnaire dans l'application. Pas d'entretien sans dossier validé.",
      questions: [
        {
          id: 'accueil_identite',
          texte: "La personne présente est bien celle de la pièce d'identité du dossier.",
          aide: "Si ce n'est pas le cas, arrêtez l'entretien.",
          type: 'oui_non',
          eliminatoire: true,
        },
      ],
    },
    {
      id: 'parcours',
      titre: 'Parcours et motivation (4 min)',
      consigne: 'Laissez le candidat parler. Aucune réponse ici ne vaut engagement de sa part.',
      questions: [
        {
          id: 'parcours_activite',
          texte: "« Parlez-moi de ce que vous faites aujourd'hui, et de ce que vous avez fait avant. »",
          aide: "Écoutez s'il a déjà livré, conduit pour un tiers ou travaillé au contact de clients.",
          type: 'texte',
        },
        {
          id: 'parcours_motivation',
          texte: '« Pourquoi voulez-vous livrer avec SwiftEats ? »',
          aide:
            "Une réponse simple et honnête (« gagner de l'argent, j'ai une moto ») vaut 3. Valorisez celui qui a compris le fonctionnement : indépendant, payé à la course.",
          type: 'note',
        },
        {
          id: 'parcours_disponibilite',
          texte: '« Combien d\'heures par semaine pensez-vous livrer, et à quels moments ? »',
          aide:
            "Information seulement : n'imposez aucun horaire, il est indépendant. Notez s'il est disponible aux heures de repas (12 h–14 h, 19 h–21 h).",
          type: 'texte',
        },
      ],
    },
    {
      id: 'bamako',
      titre: 'Connaissance de Bamako (6 min)',
      consigne: "Sans carte. On teste l'orientation, pas la mémoire des noms de rues.",
      questions: [
        {
          id: 'bamako_quartiers',
          texte: '« Quels quartiers connaissez-vous bien ? Dans lesquels êtes-vous à l\'aise pour circuler ? »',
          type: 'note',
        },
        {
          id: 'bamako_itineraire',
          texte:
            '« Vous récupérez une commande à Hamdallaye ACI 2000 et vous livrez à Kalaban Coura. Par où passez-vous ? »',
          aide:
            "Une bonne réponse cite un pont et un itinéraire cohérent, et pense aux embouteillages aux heures de pointe. Le pont exact importe moins que la logique.",
          type: 'note',
        },
        {
          id: 'bamako_adresse',
          texte:
            '« Le client vous donne comme adresse "derrière la mosquée, à côté de la boutique verte". Comment faites-vous pour le trouver ? »',
          aide: "Attendu : l'appeler, utiliser la position GPS de l'application, demander un repère.",
          type: 'note',
        },
        {
          id: 'bamako_cartes',
          texte: 'Sait utiliser Google Maps ou une application de cartes sur son téléphone.',
          type: 'oui_non',
        },
      ],
    },
    {
      id: 'equipement',
      titre: 'Véhicule, papiers et téléphone (4 min)',
      consigne: 'Vérifiez que le candidat a ce qu\'il faut pour travailler dès son activation.',
      questions: [
        {
          id: 'equipement_vehicule',
          texte: '« Le véhicule de la carte grise est-il celui que vous utiliserez ? Vous appartient-il ? »',
          aide:
            "S'il n'est pas à lui, demandez l'accord du propriétaire. L'assurance doit couvrir ce véhicule.",
          type: 'texte',
        },
        {
          id: 'equipement_casque',
          texte: 'A un casque et accepte de le porter attaché à chaque course.',
          aide: 'Un refus de porter le casque est éliminatoire.',
          type: 'oui_non',
          eliminatoire: true,
        },
        {
          id: 'equipement_telephone',
          texte: '« Quel téléphone avez-vous ? Est-ce un Android ? Avez-vous un forfait internet ? »',
          aide: "L'application exige un smartphone Android ou un iPhone, avec des données mobiles et le GPS.",
          type: 'texte',
        },
        {
          id: 'equipement_compte_mm',
          texte: 'A un compte Wave ou Orange Money à son nom.',
          aide: "C'est là qu'il sera payé. Le compte doit être à son nom.",
          type: 'oui_non',
        },
      ],
    },
    {
      id: 'client',
      titre: 'Le client et la commande (6 min)',
      consigne: 'Mises en situation : laissez-le répondre sans l\'aider, puis notez.',
      questions: [
        {
          id: 'client_absent',
          texte: '« Vous arrivez, le client ne répond pas au téléphone et ne descend pas. Que faites-vous ? »',
          aide:
            "Attendu : rappeler, prévenir via l'application, attendre le délai prévu, contacter l'équipe. Mauvaise réponse : repartir avec la commande sans prévenir.",
          type: 'note',
        },
        {
          id: 'client_renverse',
          texte: '« En route, le sac se renverse et la sauce a coulé. Que faites-vous ? »',
          aide: "Attendu : prévenir l'équipe et le client honnêtement. Mauvaise réponse : cacher le problème.",
          type: 'note',
        },
        {
          id: 'client_enerve',
          texte: '« Le client est énervé à cause du retard et vous parle mal. Comment réagissez-vous ? »',
          aide: "Attendu : rester calme, s'excuser, ne pas répondre sur le même ton, signaler si besoin.",
          type: 'note',
        },
        {
          id: 'client_especes',
          texte:
            '« Le client vous dit : "Je n\'ai pas payé dans l\'application, je te donne l\'argent en main." Que faites-vous ? »',
          aide:
            "Attendu : refuser, tout est payé par mobile money avant la livraison. Notez 1 si la réponse montre qu'il garderait l'argent pour lui : c'est éliminatoire.",
          type: 'note',
          eliminatoire: true,
        },
        {
          id: 'client_isotherme',
          texte: '« Pourquoi transporte-t-on la nourriture dans un sac isotherme fermé ? »',
          aide: "Hygiène, température, présentation. Une réponse qui montre qu'il s'en moque est un signal négatif.",
          type: 'note',
        },
      ],
    },
    {
      id: 'integrite',
      titre: 'Intégrité et sécurité (4 min)',
      consigne: 'Abordez le casier judiciaire avec tact, et seulement s\'il porte une mention.',
      questions: [
        {
          id: 'integrite_annulee',
          texte: '« Une commande est annulée alors que vous l\'avez déjà récupérée. Que faites-vous du repas ? »',
          aide:
            "Attendu : suivre la consigne de l'application ou demander à l'équipe. Pas : le garder ou le revendre de sa propre initiative.",
          type: 'note',
        },
        {
          id: 'integrite_casier',
          texte:
            'Le casier judiciaire ne porte aucune condamnation incompatible avec l\'activité (vol, violence, agression, trafic).',
          aide:
            "Répondez « non » si une telle condamnation figure : c'est éliminatoire. Les autres mentions s'apprécient (nature, ancienneté, explications) : en cas de doute, recommandez « revoir ».",
          type: 'oui_non',
          eliminatoire: true,
        },
        {
          id: 'integrite_casier_explications',
          texte: 'Si le casier porte une mention : explications du candidat.',
          type: 'texte',
        },
        {
          id: 'integrite_route',
          texte: '« Vous est-il déjà arrivé d\'avoir un accident ou une amende ? Comment vous protégez-vous sur la route ? »',
          type: 'note',
        },
      ],
    },
    {
      id: 'questions',
      titre: 'Ses questions (2 min)',
      consigne:
        "« Avez-vous des questions ? » Répondez honnêtement : rémunération affichée avant chaque course, paie le 1er et le 16, liberté d'horaires et de refus. Ne promettez aucun revenu mensuel.",
      questions: [
        {
          id: 'questions_notes',
          texte: 'Questions posées et points à éclaircir.',
          type: 'texte',
        },
      ],
    },
  ],
  synthese: {
    criteres: [
      { id: 'bamako', libelle: 'Connaissance de Bamako' },
      { id: 'client', libelle: 'Comportement avec le client' },
      { id: 'integrite', libelle: 'Honnêteté et intégrité' },
      { id: 'securite', libelle: 'Sécurité routière' },
      { id: 'hygiene', libelle: 'Hygiène et soin de la commande' },
      { id: 'equipement', libelle: 'Équipement (téléphone, compte mobile money, véhicule)' },
    ],
  },
}

export default CANEVAS_ENTRETIEN
